"use client"

import { useRouter } from "@bprogress/next/app";
import { Box, colors, useTheme, type SxProps, type Theme } from "@mui/material";
import { getLandingComponent } from "@/app/(pages)/fa/v2/(shell)/landing/[slug]/landingComponentRegistry";
import mediumZoom, { Zoom } from "medium-zoom";
import { useEffect, useMemo, useRef } from "react";
import RecentLinkHighlighter, { isInternalAppUrl, recentLinkHighlighterSx } from "./RecentLinkHighlighter";

type RenderQuillSegment =
    | { type: "html"; html: string }
    | { type: "landing-component"; name: string; content: Record<string, unknown> };

const LANDING_COMPONENT_RE = /<div\b(?=[^>]*(?:data-quill-landing-component=["']1["']|class=["'][^"']*\bquill-landing-component\b[^"']*["']))[^>]*>[\s\S]*?<\/div>/gi;
const MAX_EMBEDDED_COMPONENT_DEPTH = 2;

export default function RenderQuillContent({ html, embeddedDepth = 0, embeddedComponentSx }: {
    /**
     * Raw html text
     */
    html: string;
    embeddedDepth?: number;
    embeddedComponentSx?: SxProps<Theme>;
}) {
    const theme = useTheme()
    const router = useRouter();
    const contentRef = useRef<HTMLDivElement>(null);
    const zoomRef = useRef<Zoom | null>(null);
    const fallbackAlt = 'تصویر مربوط به محتوای این بخش';
    const fallbackTitle = 'مشاهده تصویر با کیفیت بهتر';

    const mediaBase = process.env.NEXT_PUBLIC_MEDIA_URL ?? '';
    const withAbsolutePaths = mediaBase
        ? html.replace(/(<img\s[^>]*src=")\/api\/v1\/media\//g, `$1${mediaBase}/api/v1/media/`)
        : html;

    const optimizedHTML = withAbsolutePaths
        // new images are wrapped in <figure class="quill-image-embed"> by the blot and need no processing.
        // old images are bare <img> tags — catch only those (figure branch returns as-is, img branch gets optimized + wrapped).
        .replace(
            /(<figure\s[^>]*class="quill-image-embed"[\s\S]*?<\/figure>)|(<img\s+[^>]*src="(https?:\/\/[^">]+\/api\/v1\/media\/[^">]+?)"[^>]*>)/g,
            (_match, figure, _img, src) => {
                if (figure) return figure;
                const separator = src.includes("?") ? "&" : "?";
                const optimizedSrc = `${src}${separator}width=1080&quality=80`;
                return `<span class="ql-image-wrapper"><img loading="lazy" src="${optimizedSrc}" decoding="async" fetchPriority="low" data-zoom-src="${src}" alt="${fallbackAlt}" title="${fallbackTitle}" /></span>`;
            }
        )
        .replace(
            /<table\b([^>]*)>([\s\S]*?)<\/table>/g,
            (_match, attrs: string, body: string) => {
                // the editor stamps width:100% on the table; inline styles beat the sx rules below,
                // so drop it and let the stylesheet decide how wide the table gets.
                const cleaned = attrs.replace(/\bwidth\s*:\s*100%\s*;?/gi, '');
                return `<div class="ql-table-wrapper"><table data-scroll-wrapped="1"${cleaned}>${body}</table></div>`;
            }
        )
        .replace(/<a\b[^>]*>(\s|&nbsp;)*<\/a>/gi, '')
        .replace(/<p\b[^>]*>(\s|&nbsp;)*<\/p>/gi, '')
        .replace(/(href="https?:\/\/[^"]*?")/g, (m) => m.replace(/([^:])\/\//g, '$1/'))
        .replace(
            /<a\b([^>]*)href="([^"]+)"([^>]*)>\s*(<span class="ql-image-wrapper">[\s\S]*?<\/span>)\s*<\/a>/gi,
            (match, before, href, after, imageWrapper) => {
                if (/\baria-label\s*=/i.test(`${before} ${after}`)) return match;
                return `<a${before}href="${href}"${after} aria-label="مشاهده تصویر لینک شده">${imageWrapper}</a>`;
            }
        )
        .replace(/<iframe\b([^>]*)>/gi, (match, attrs) => {
            if (/\btitle\s*=/i.test(attrs)) return match;
            return `<iframe${attrs} title="ویدیو">`;
        })
        .replace(/[\r\n]/g, '');

    const segments = useMemo(() => parseQuillSegments(optimizedHTML), [optimizedHTML]);

    useEffect(() => {
        if (!contentRef.current) return;

        // Wrap tables in a scrollable container
        const tables = Array.from(contentRef.current.querySelectorAll<HTMLTableElement>("table:not([data-scroll-wrapped])"))
            .filter((table) => !table.closest("[data-quill-landing-component-render]"));
        tables.forEach((table) => {
            const wrapper = document.createElement("div");
            wrapper.style.overflowX = "auto";
            wrapper.style.marginBottom = "16px";
            table.setAttribute("data-scroll-wrapped", "1");
            table.parentNode?.insertBefore(wrapper, table);
            wrapper.appendChild(table);
        });

        const applyZoom = () => {
            const images = Array.from(contentRef.current!.querySelectorAll<HTMLImageElement>("img"))
                .filter((img) => !img.closest("a") && !img.closest("[data-quill-landing-component-render]"));
            if (images.length === 0) return;
            if (zoomRef.current) zoomRef.current.detach();
            zoomRef.current = mediumZoom(images, {
                margin: 16,
                background: "rgba(0,0,0,0.85)",
            });
        };

        applyZoom();

        const observer = new MutationObserver(() => applyZoom());
        observer.observe(contentRef.current, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            zoomRef.current?.detach();
        };
    }, [optimizedHTML]);

    useEffect(() => {
        const contentEl = contentRef.current;
        if (!contentEl) return;

        const onAnchorClick = (event: MouseEvent) => {
            if (event.defaultPrevented || event.button !== 0) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

            const target = event.target as HTMLElement | null;
            const anchor = target?.closest("a");
            if (!anchor) return;
            if (anchor.closest("[data-quill-landing-component-render]")) return;
            if (anchor.hasAttribute("download")) return;

            const href = anchor.getAttribute("href");
            if (!href) return;

            let nextUrl: URL;
            let currentUrl: URL;
            try {
                currentUrl = new URL(window.location.href);
                nextUrl = new URL(href, currentUrl);
            } catch {
                return;
            }

            if (!isInternalAppUrl(nextUrl)) return;

            event.preventDefault();
            router.push(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
        };

        contentEl.addEventListener("click", onAnchorClick);
        return () => {
            contentEl.removeEventListener("click", onAnchorClick);
        };
    }, [optimizedHTML, router]);

    return (
        <RecentLinkHighlighter scope="quill" allowBlankTarget>
            {({ ref, onClickCapture }) => (
                <Box
                    ref={ref}
                    onClickCapture={onClickCapture}
                    sx={{ display: 'contents', ...recentLinkHighlighterSx }}
                >
                    <Box
                        ref={contentRef}
                        className="ql-wrapper"
                        sx={{
                // ── Font ─────────────────────────────────────────────
                '& [data-quill-html-segment]': {
                    lineHeight: 2.15,
                    fontFamily: 'var(--main-font),sans-serif !important',

                    '& *': {
                        fontFamily: 'var(--main-font),sans-serif !important',
                    },

                    // ── Quill alignment / direction classes ──────────────
                    '& .ql-direction-rtl': { direction: 'ltr' },
                    '& .ql-direction-ltr': { direction: 'rtl' },
                    '& .ql-align-right':   { textAlign: 'left' },
                    '& .ql-align-left':    { textAlign: 'right' },
                    '& .ql-align-justify': { textAlign: 'justify' },
                    '& .ql-align-center':  { textAlign: 'center' },

                    // ── Headings ─────────────────────────────────────────
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                        lineHeight: 1.4,
                        mt: 3,
                        mb: 1,
                    },
                    '& h1': {
                        mt: 0,
                        mb: 1,
                        fontSize: 30,
                        fontWeight: 700,
                        color: colors.grey[900],
                        '& strong': { fontWeight: 800 },
                        [theme.breakpoints.down('md')]: { fontSize: 26 },
                    },
                    '& h2': {
                        mt: 4,
                        mb: 1,
                        fontSize: 22,
                        fontWeight: 600,
                        color: colors.grey[900],
                        '& strong': { fontWeight: 700 },
                        [theme.breakpoints.down('md')]: { fontSize: 19 },
                    },
                    '& h3': {
                        fontSize: 20,
                        color: colors.grey[800],
                        [theme.breakpoints.down('md')]: { fontSize: 18 },
                    },
                    '& h4': {
                        fontSize: 18,
                        color: colors.grey[800],
                        [theme.breakpoints.down('md')]: { fontSize: 17 },
                    },

                    // ── Quill font sizes (manually set in editor — highest priority) ──
                    '& .ql-size-small':  { fontSize: '12px !important', [theme.breakpoints.down('md')]: { fontSize: '11px !important' } },
                    '& .ql-size-large':  { fontSize: '32px !important', [theme.breakpoints.down('md')]: { fontSize: '26px !important' } },
                    '& .ql-size-huge':   { fontSize: '42px !important', [theme.breakpoints.down('md')]: { fontSize: '34px !important' } },

                    // ── Body text ────────────────────────────────────────
                    '& strong': { fontWeight: 600 },
                    '& p': {
                        m: 0,
                        mb: 2,
                        fontSize: 15,
                        fontWeight: 400,
                        color: colors.grey[800],
                        [theme.breakpoints.down('md')]: { fontSize: 13 },
                    },
                    '& blockquote': {
                        my: 3,
                        mx: 0,
                        px: 2.5,
                        py: 1.5,
                        borderRight: `4px solid ${colors.grey[300]}`,
                        borderRadius: '0 4px 4px 0',
                        backgroundColor: colors.grey[50],
                        color: colors.grey[700],
                        fontStyle: 'italic',
                        '& p': { mb: 0 },
                    },
                    '& a': {
                        color: colors.red[900],
                        textDecoration: 'underline',
                        '&:hover': { textDecoration: 'underline' },
                    },

                    // ── Lists ────────────────────────────────────────────
                    '& ol': {
                        listStyle: 'decimal',
                        paddingLeft: 4,
                        [theme.breakpoints.down('md')]: { paddingLeft: 2 },
                    },
                    '& ul': { listStyle: 'disc' },
                    '& li': {
                        pl: 1,
                        mt: 0.5,
                        fontSize: 14,
                        lineHeight: 2,
                        color: colors.grey[800],
                        [theme.breakpoints.down('md')]: { fontSize: 13 },
                        '&[data-list="ordered"]': {
                            listStyle: 'number',
                            '&::marker': { textAlign: 'left !important' },
                        },
                    },

                    // ── Images ───────────────────────────────────────────
                    '& .quill-image-embed': {
                        maxWidth: '100%',
                        margin: 0,
                    },
                    '& .ql-image-wrapper': {
                        position: 'relative',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        my: 2,
                        '& img': {
                            maxWidth: '100%',
                            borderRadius: 1,
                            '&:hover': { cursor: 'pointer' },
                        },
                    },
                    '& a .ql-image-wrapper': {
                        '&::after': {
                            content: '"↗"',
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.72)',
                            color: colors.common.white,
                            fontSize: 16,
                            lineHeight: 1,
                            textDecoration: 'none',
                            pointerEvents: 'none',
                        },
                    },

                    // ── Video ────────────────────────────────────────────
                    '& .quill-video-wrapper': {
                        display: 'flex',
                        justifyContent: 'center',
                        '& > .quill-video-iframe': {
                            width: '100%',
                            maxWidth: 1080,
                            aspectRatio: '16 / 9',
                        },
                    },
                    '& .ql-video': {
                        width: 1,
                        aspectRatio: '16 / 9',
                        borderRadius: 1.5,
                        overflow: 'hidden',
                    },

                    // ── Table ────────────────────────────────────────────
                    '& .ql-table-wrapper': {
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        direction: 'ltr',
                        display: 'block',
                        // auto, not 100%: a fixed width turns a caller's negative margin into a shift
                        // instead of letting the scroller widen to the screen edge.
                        width: 'auto',
                        pb: 2,
                        mb: 0,
                        '& > .ql-table-temporary': { height: 0 },
                    },
                    '& table': {
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                        // quill-table-better has no RTL support, so the panel renders its tables ltr
                        // (QuillInput.tsx writes direction:'rtl', which stylis-plugin-rtl flips to ltr).
                        // Match that here or every column comes out mirrored against what the author wrote.
                        // The wrapper stays rtl, so the scroll still opens on the first column.
                        direction: 'rtl',
                        // fill the column on a wide screen; on a phone the cell min widths below push the
                        // table past this and the wrapper scrolls, rather than squeezing every column
                        width: '100%',
                        maxWidth: 'none',
                        fontSize: 14,
                        border: `1px solid ${colors.grey[200]}`,
                        borderRadius: 2,
                        backgroundColor: colors.common.white,
                        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 18px -14px rgba(15, 23, 42, 0.3)',
                        overflow: 'hidden',
                        [theme.breakpoints.down('md')]: { fontSize: 13 },
                        '& p': { m: 0, mb: 0 },
                        '& th, & td': {
                            border: 0,
                            borderBottom: `1px solid ${colors.grey[200]}`,
                            borderLeft: `1px solid ${colors.grey[200]}`,
                            padding: '10px 14px',
                            // the panel writes width:100% inline on the table, which on a phone squeezes
                            // every column down to one word per line. A cell floor pushes the table past
                            // the viewport instead, so the wrapper scrolls and the text stays readable.
                            minWidth: 150,
                            // the editor centres cell text vertically; match it
                            verticalAlign: 'middle',
                            lineHeight: 1.9,
                            color: colors.grey[800],
                        },
                        '& th:last-of-type, & td:last-of-type': { borderLeft: 0 },
                        '& tr:last-of-type th, & tr:last-of-type td': { borderBottom: 0 },
                        '& th': {
                            fontWeight: 700,
                            color: colors.grey[900],
                            // grey[100] is the page background itself, so the header row used to vanish
                            // into it. Slate 200 keeps the same family as the striping and the shadow.
                            backgroundColor: '#e2e8f0',
                            '& > p': { fontWeight: 700, color: colors.grey[900] },
                        },
                        '& tbody tr': { transition: 'background-color 160ms ease' },
                        '& tr:nth-of-type(even)': { backgroundColor: 'rgba(248, 250, 252, 0.72)' },
                        '& tbody tr:hover': { backgroundColor: 'rgba(239, 246, 255, 0.72)' },
                    },
                },

                '& [data-quill-landing-component-render]': {
                    my: 2,
                    isolation: 'isolate',
                },
            }}
                    >
                        {segments.map((segment, index) => (
                            segment.type === "html" ? (
                                <Box
                                    key={`html-${index}`}
                                    component="div"
                                    data-quill-html-segment="1"
                                    sx={{ display: 'contents' }}
                                    dangerouslySetInnerHTML={{ __html: segment.html }}
                                />
                            ) : (
                                <EmbeddedLandingComponent
                                    key={`landing-${index}`}
                                    name={segment.name}
                                    content={segment.content}
                                    embeddedDepth={embeddedDepth ?? 0}
                                    sx={embeddedComponentSx}
                                />
                            )
                        ))}
                    </Box>
                </Box>
            )}
        </RecentLinkHighlighter>
    )
}

function EmbeddedLandingComponent({
    name,
    content,
    embeddedDepth,
    sx,
}: {
    name: string;
    content: Record<string, unknown>;
    embeddedDepth: number;
    sx?: SxProps<Theme>;
}) {
    if (embeddedDepth >= MAX_EMBEDDED_COMPONENT_DEPTH) return null;

    const Component = getLandingComponent(name);
    if (!Component) return null;

    const LandingComponent = Component as any;

    return (
        <Box data-quill-landing-component-render="1" sx={sx}>
            <LandingComponent content={content} embeddedDepth={embeddedDepth + 1} />
        </Box>
    );
}

function parseQuillSegments(html: string): RenderQuillSegment[] {
    const segments: RenderQuillSegment[] = [];
    let lastIndex = 0;

    html.replace(LANDING_COMPONENT_RE, (match, offset: number) => {
        if (offset > lastIndex) {
            segments.push({ type: "html", html: html.slice(lastIndex, offset) });
        }

        const name = readHtmlAttr(match, "data-component-name");
        const encodedContent = readHtmlAttr(match, "data-component-content");
        const content = parseEmbeddedContent(encodedContent);

        if (name) {
            segments.push({ type: "landing-component", name, content });
        }

        lastIndex = offset + match.length;
        return match;
    });

    if (lastIndex < html.length) {
        segments.push({ type: "html", html: html.slice(lastIndex) });
    }

    return segments.length ? segments : [{ type: "html", html }];
}

function readHtmlAttr(html: string, attr: string) {
    const pattern = new RegExp(`${attr}=(["'])(.*?)\\1`, "i");
    return html.match(pattern)?.[2] ?? "";
}

function parseEmbeddedContent(encodedContent: string): Record<string, unknown> {
    if (!encodedContent) return {};

    try {
        return JSON.parse(decodeURIComponent(encodedContent));
    } catch {
        return {};
    }
}
